import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import WGS84Service from '@/services/wgs84ProjectionService';
import get from 'lodash/get';

export default {
  data: function () {
    return {
      camera: {
        center: [0, 0],
        zoom: 13,
      },
      markerItems: [],
      dtwinClusters: [],
      polygons: [],
      hasLoadedInitially: false,

      boundingBox: {},
      mapBoundsLock: [],
      selectedCluster: {},

      stateKeys: [],
      twinTags: [],
      floorList: [],

      isIndoorQuery: false,
      indoorEnabled: false,
      indoorBuildingId: null,
      indoorFloorDtwinId: null,
      indoorFloorDtwin: null,

      enableZoomLock: false,

      persistSettings: true,
      // eslint-disable-next-line eqeqeq
      excludeZones: localStorage.getItem('map_excludeZones') == 'true' ? true : false,
      selectedTwinTag: localStorage.getItem('map_selectedTwinTag') || null,
      selectedStateKey: localStorage.getItem('map_selectedStateKey') || null,
      // eslint-disable-next-line eqeqeq
      hideStatelessPolygons: localStorage.getItem('map_hideStatelessPolygons') == 'true' || false,
      // eslint-disable-next-line eqeqeq
      gradientPolygonColours: localStorage.getItem('map_gradientPolygonColours') == 'true' ? true : false,

      mapBaseLink: '/digitaltwin-mgmt/map/twin/',
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),

    groupedFloors() {
      let groupedByBuildingId = {};
      this.floorList.forEach((floor) => {
        if (this.selectedAccounts && this.selectedAccounts.length > 0) {
          // if (in the asset-map) there is an account-filter, only show building/floors for that account(s)
          if (this.selectedAccounts.indexOf(floor.tenantId) < 0) {
            return;
          }
        }
        const buildingId = `${get(floor, 'building.id') || 'NONE'}`;
        if (!groupedByBuildingId[buildingId]) {
          groupedByBuildingId[buildingId] = {
            building: get(floor, 'building'),
            floors: [],
          };
        }
        groupedByBuildingId[buildingId].floors.push({
          ...floor,
          title: `${floor.title} ${floor.floorNumber !== null ? `(${floor.floorNumber})` : ''}`,
        });
      });
      Object.keys(groupedByBuildingId).forEach((buildingId) => {
        groupedByBuildingId[buildingId].floors.sort((a, b) => {
          return a.title.localeCompare(b.title);
        });
      });
      return groupedByBuildingId;
    },
    buildingFloorList() {
      if (this.indoorBuildingId) {
        let floorList = (this.groupedFloors[this.indoorBuildingId] || {}).floors || [];
        floorList.sort((a, b) => {
          return (b.floorNumber || b.title).localeCompare(a.floorNumber || a.title);
        });
        return floorList;
      }
      return null;
    },
    buildingList() {
      return Object.values(this.groupedFloors)
        .map((data) => {
          return (
            data.building || { id: 'NONE', title: `- ${this.$i18n_t('DigitalTwin.DigitalTwin.map.selectionNone')} -` }
          );
        })
        .sort((a, b) => {
          if (a.id === 'NONE') {
            return -1;
          }
          if (b.id === 'NONE') {
            return 1;
          }
          return a.title.localeCompare(b.title);
        });
    },
    dtwinOverlayInfo() {
      if (!this.indoorEnabled || !get(this.indoorFloorDtwin, 'data.indoor.overlayImageId')) {
        return null;
      }
      const ne = {
        lat: parseFloat(this.indoorFloorDtwin.data.indoor.neLat),
        lon: parseFloat(this.indoorFloorDtwin.data.indoor.neLon),
      };
      if (!WGS84Service.isValidWgs84(ne)) {
        return null;
      }
      const sw = {
        lat: parseFloat(this.indoorFloorDtwin.data.indoor.swLat),
        lon: parseFloat(this.indoorFloorDtwin.data.indoor.swLon),
      };
      if (!WGS84Service.isValidWgs84(sw)) {
        return null;
      }
      // https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/#image
      // https://docs.mapbox.com/mapbox-gl-js/example/image-on-a-map/
      return {
        image: `${process.env.VUE_APP_API_HOST}${process.env.VUE_APP_API_PATH}/upload/${this.indoorFloorDtwin.data.indoor.overlayImageId}`,
        coordinates: [
          [sw.lon, ne.lat],
          [ne.lon, ne.lat],
          [ne.lon, sw.lat],
          [sw.lon, sw.lat],
        ],
      };
    },
    stateKeySelectionItems() {
      return [{ value: null, text: `- ${this.$i18n_t('DigitalTwin.DigitalTwin.states.dataSources.none')} -` }]
        .concat(
          this.stateKeys.map((k) => {
            return { value: k, text: k };
          })
        )
        .concat(
          // the settings are persisted across "map-sessions", so if a different twin is selected with different states
          // still keep the currently selected key as "fallback" here
          this.selectedStateKey && this.stateKeys.findIndex((k) => k === this.selectedStateKey) < 0
            ? [{ value: this.selectedStateKey, text: this.selectedStateKey }]
            : []
        );
    },
    twinTagItems() {
      return [{ value: null, text: `(${this.$i18n_t('DigitalTwin.DigitalTwin.twinDisabled')})` }]
        .concat(
          this.twinTags.map((k) => {
            return { value: k, text: k };
          })
        )
        .concat(
          // the settings are persisted across "map-sessions", so if a different twin is selected with different tags
          // still keep the currently selected tag as "fallback" here
          this.selectedTwinTag && this.twinTags.findIndex((t) => t === this.selectedTwinTag) < 0
            ? [{ value: this.selectedTwinTag, text: this.selectedTwinTag }]
            : []
        );
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  watch: {
    excludeZones() {
      this.persistMapSettingValue('excludeZones', this.excludeZones);
      this.$refs.mapInstance.debouncedRequestClusterRequest();
    },
    // if the state-keys are updated, check if the list still contains the selected/persisted key,
    // in order to reset it for reduced user-confusion
    // https://digimondo.atlassian.net/browse/RD-6254
    stateKeys() {
      let persistedOrSelectedKey = this.selectedStateKey;
      if (persistedOrSelectedKey === null) {
        persistedOrSelectedKey = localStorage.getItem('map_selectedStateKey') || null;
      }
      // check if runtime "selected key" needs to be reset or restored
      if (persistedOrSelectedKey && this.stateKeys.findIndex((k) => k === persistedOrSelectedKey) < 0) {
        this.selectedStateKey = null;
      } else {
        this.selectedStateKey = persistedOrSelectedKey;
      }
    },
    selectedStateKey() {
      if (this.selectedStateKey) {
        // only persist a SELECTED state-key
        // if reset, keep the current persisted one, see above in `stateKeys` watcher
        this.persistMapSettingValue('selectedStateKey', this.selectedStateKey);
      }
      this.$refs.mapInstance.debouncedRequestClusterRequest();
    },
    hideStatelessPolygons() {
      this.persistMapSettingValue('hideStatelessPolygons', this.hideStatelessPolygons);
      this.$refs.mapInstance.debouncedRequestClusterRequest();
    },
    selectedTwinTag() {
      this.persistMapSettingValue('selectedTwinTag', this.selectedTwinTag);
      this.$refs.mapInstance.debouncedRequestClusterRequest();
    },
    gradientPolygonColours() {
      this.persistMapSettingValue('gradientPolygonColours', this.gradientPolygonColours);
    },
    indoorEnabled() {
      if (this.indoorEnabled) {
        this.indoorBuildingId = get(this.buildingList, '[0].id');
      } else {
        this.indoorBuildingId = null;
      }
    },
    indoorBuildingId() {
      this.indoorFloorDtwinId = get(this.groupedFloors, `[${this.indoorBuildingId}].floors[0].id`);
      this.mapBoundsLock = [];
      this.hasLoadedInitially = false;
      // the building has changed (one was selected; or the indoor-mode was de-selected)
      // in any case, zoom to the bounding-box that fits the entire building / world with all twins
      return this.loadTree().then(() => {
        this.$refs.mapInstance.flyToCamera();
      });
    },
    indoorFloorDtwinId() {
      this.fetchFloorPlanTwin()
        .then(() => {
          if (!this.indoorBuildingId) {
            this.mapBoundsLock = [];
            this.hasLoadedInitially = false;
            // the floor has changed without a building attached, so the relevant bounds are possibly different
            // in any case, zoom to the bounding-box that fits the entirety of floor-twins
            return this.loadTree().then(() => {
              this.$refs.mapInstance.flyToCamera();
            });
          }
        })
        .catch((error) => {
          console.error(error);
        });
      // if a building was indeed selected, then just perform a simple cluster-request,
      // the bounds do not change
      if (this.indoorBuildingId) {
        this.$refs.mapInstance.debouncedRequestClusterRequest();
      }
    },
    persistSettings() {
      const valuesToPersist = this.valuesToPersist();
      Object.keys(valuesToPersist).forEach((key) => {
        this.persistMapSettingValue(key, valuesToPersist[key]);
      });
    },
    dtwinOverlayInfo() {
      if (this.mapBoundsLock && this.mapBoundsLock.length && this.enableZoomLock) {
        let southWest = this.mapBoundsLock[0];
        let northEast = this.mapBoundsLock[1];
        // "reverse" the spread from calculate bounds
        const latSpread = Math.abs(Math.abs(northEast[1]) - Math.abs(southWest[1])) / 1.15;
        const lonSpread = Math.abs(Math.abs(northEast[0]) - Math.abs(southWest[0])) / 1.15;
        southWest = { lon: southWest[0] - lonSpread, lat: southWest[1] - latSpread };
        northEast = { lon: northEast[0] + lonSpread, lat: northEast[1] + latSpread };
        this.mapBoundsLock = [];
        this.calculateMapBoundsLock(northEast, southWest);
      }
    },
    enableZoomLock() {
      this.mapBoundsLock = [];
      this.hasLoadedInitially = false;
      // the floor has changed without a building attached, so the relevant bounds are possibly different
      // in any case, zoom to the bounding-box that fits the entirety of floor-twins
      return this.loadTree().then(() => {
        this.$refs.mapInstance.flyToCamera();
      });
    },
    $route() {
      const changedIndoor = this.getIndoorFromQueryAndReturnIfChanged();
      this.fetchTwinData()
        .then(() => {
          if (changedIndoor) {
            this.indoorEnabled = this.isIndoorQuery;
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },
  methods: {
    // removed (from) mixin mounted-hook, should be called from each Map view explicitly for more data-loading control
    loadInitialData() {
      const changedIndoor = this.getIndoorFromQueryAndReturnIfChanged();
      this.fetchTwinData()
        .then(() => {
          if (changedIndoor) {
            this.indoorEnabled = this.isIndoorQuery;
          } else {
            this.indoorEnabled = get(this.$route, 'query.indoor') === 'true' ? true : false;
          }
        })
        .catch((e) => {
          console.error(e);
        });
    },
    getIntFromQueryParam(paramName, defaultValue) {
      const paramValue = parseInt(get(this.$route, `query.${paramName}`));
      if (isNaN(paramValue) || paramValue < 0) {
        return defaultValue;
      }
      return paramValue;
    },
    getIndoorFromQueryAndReturnIfChanged() {
      let isIndoorQuery = false;

      const queryParams = (window.location.href || '').split('?');
      if (queryParams.length >= 2) {
        isIndoorQuery = queryParams[1].split('&').findIndex((v) => /(^indoor(=true|$))/gi.exec(v) !== null) >= 0;
      }

      const changed = this.isIndoorQuery !== isIndoorQuery;
      this.isIndoorQuery = isIndoorQuery;
      return changed;
    },
    valuesToPersist() {
      return {
        gradientPolygonColours: this.gradientPolygonColours,
        excludeZones: this.excludeZones,
        selectedStateKey: this.selectedStateKey,
        selectedTwinTag: this.selectedTwinTag,
      };
    },
    digitalTwinChanged(selection) {
      if (parseInt(selection.id) !== parseInt(this.$route.params.twinId)) {
        this.$router.push(`${this.mapBaseLink}${selection.id}`);
      }
    },
    fetchFloorPlanTwin() {
      if (this.indoorFloorDtwinId) {
        return ApiService.digitalTwinGet(this, this.indoorFloorDtwinId, false)
          .then((res) => {
            this.indoorFloorDtwin = res;
          })
          .catch((error) => {
            console.error('Error loading indoor-floor-twin', error);
          });
      }
      return Promise.resolve();
    },
    persistMapSettingValue(key, value) {
      if (this.persistSettings && value) {
        localStorage.setItem(`map_${key}`, value);
      } else {
        localStorage.removeItem(`map_${key}`);
      }
    },
    resetDefaults() {
      this.excludeZones = false;
      this.selectedStateKey = null;
      this.selectedTwinTag = null;
      this.gradientPolygonColours = false;
    },
    calculateBoundingBox(data) {
      this.boundingBox = data.boundingBox;

      if (this.boundingBox.coordinates) {
        let northEast = { lat: -180, lon: -360 };
        let southWest = { lat: 180, lon: 360 };
        // in the case that there is only 1 twin, the bounding-box will just be a coordiante, rather than a bounding-box
        // so transform it here in order for the following stuff to work
        if (this.boundingBox.coordinates.length === 2 && typeof this.boundingBox.coordinates[0] === 'number') {
          this.boundingBox.coordinates = [this.boundingBox.coordinates];
        }
        this.boundingBox.coordinates.forEach((c) => {
          northEast.lat = Math.max(northEast.lat, c[1]);
          southWest.lat = Math.min(southWest.lat, c[1]);
          northEast.lon = Math.max(northEast.lon, c[0]);
          southWest.lon = Math.min(southWest.lon, c[0]);
        });
        // extend the default bounding-box slightly so "everything is visible" --> RD-5198
        const spreadBounds = this.calculateExtendedBoundingBox(northEast, southWest);
        northEast = { lon: spreadBounds[1][0], lat: spreadBounds[1][1] };
        southWest = { lon: spreadBounds[0][0], lat: spreadBounds[0][1] };

        const mapContainer = this.$refs.mapInstance.$el;
        const mapDimensions = { width: mapContainer.clientWidth - 30, height: mapContainer.clientHeight - 30 };
        const zoomToUse = this.getIntFromQueryParam(
          'zoom',
          WGS84Service.boundsZoomLevel({ northEast, southWest }, mapDimensions) - 0.5
        );
        this.camera = {
          center: data.boundingBox.centroid,
          zoom: zoomToUse,
        };
        this.calculateMapBoundsLock(northEast, southWest);
      }
      this.dtwinClusters = data.clusters;
      this.polygons = data.polygons;

      if (this.hasLoadedInitially) {
        return;
      }
      this.hasLoadedInitially = true;
      // INITIALLY, an unfiltered bounding-box request was sent,
      // in case there are filters persisted, send a second request as soon as that finished
      if (this.selectedStateKey || this.selectedTwinTag) {
        this.$refs.mapInstance.debouncedRequestClusterRequest();
      }
    },
    safeCalculateBoundingBoxSpread(northEast, southWest) {
      const latSpread = Math.abs(Math.abs(northEast.lat) - Math.abs(southWest.lat));
      const lonSpread = Math.abs(Math.abs(northEast.lon) - Math.abs(southWest.lon));

      const minSpread = 0.000005;
      if (latSpread < minSpread) {
        northEast.lat += minSpread * 0.5;
        southWest.lat -= minSpread * 0.5;
      }
      if (lonSpread < minSpread) {
        northEast.lon += minSpread * 0.5;
        southWest.lon -= minSpread * 0.5;
      }
    },
    calculateExtendedBoundingBox(northEast, southWest) {
      if (this.dtwinOverlayInfo) {
        // coordinates: [
        //   [sw.lon, ne.lat],
        //   [ne.lon, ne.lat],
        //   [ne.lon, sw.lat],
        //   [sw.lon, sw.lat],
        // ],
        northEast.lat = Math.max(northEast.lat, this.dtwinOverlayInfo.coordinates[0][1]);
        southWest.lat = Math.min(southWest.lat, this.dtwinOverlayInfo.coordinates[2][1]);
        northEast.lon = Math.max(northEast.lon, this.dtwinOverlayInfo.coordinates[2][0]);
        southWest.lon = Math.min(southWest.lon, this.dtwinOverlayInfo.coordinates[0][0]);
      }
      this.safeCalculateBoundingBoxSpread(northEast, southWest);

      // the bigger the coordinate-spread, the smaller the "edges" should be around the polygon
      // the back-end returns a bigger polygon anyway for the clustered bounding-box, so should not need to be extended too much
      const minMaxSpread = [0.000005, 0.00009];
      const minMaxFactor = [1.1, 1.01];
      const spreads = {};
      ['lat', 'lon'].forEach((dimension) => {
        let spread = Math.abs(Math.abs(northEast[dimension]) - Math.abs(southWest[dimension]));
        const currSpread = Math.min(Math.max(spread, minMaxSpread[0]), minMaxSpread[1]);
        const gradient = (currSpread - minMaxSpread[0]) / (minMaxSpread[1] - minMaxSpread[0]);
        const spreadFactor = minMaxFactor[0] - (minMaxFactor[0] - minMaxFactor[1]) * gradient;
        spreads[dimension] = spread * spreadFactor;
      });

      const normaliseLon = (lon) => Math.max(-179, Math.min(179, lon));
      const normaliseLat = (lat) => Math.max(-89, Math.min(89, lat));

      return [
        [normaliseLon(southWest.lon - spreads['lon']), normaliseLat(southWest.lat - spreads['lat'])],
        [normaliseLon(northEast.lon + spreads['lon']), normaliseLat(northEast.lat + spreads['lat'])],
      ];
    },
    calculateMapBoundsLock(northEast, southWest) {
      if (this.mapBoundsLock.length === 0 && this.enableZoomLock) {
        const mapBoundsLock = this.calculateExtendedBoundingBox(northEast, southWest);
        this.mapBoundsLock = mapBoundsLock;
      }
    },
  },
};
